/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { assert, unreachableCase } from "@fluidframework/core-utils/internal";
import { ValueType } from "@fluidframework/shared-object-base/internal";
import { LocalValueMaker, makeSerializable } from "./localValues.js";
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access */
function isMapKeyLocalOpMetadata(metadata) {
    return (metadata !== undefined &&
        typeof metadata.pendingMessageId === "number" &&
        (metadata.type === "add" || metadata.type === "edit"));
}
function isClearLocalOpMetadata(metadata) {
    return (metadata !== undefined &&
        metadata.type === "clear" &&
        typeof metadata.pendingMessageId === "number");
}
function isMapLocalOpMetadata(metadata) {
    return (metadata !== undefined &&
        typeof metadata.pendingMessageId === "number" &&
        (metadata.type === "add" || metadata.type === "edit" || metadata.type === "clear"));
}
/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access */
function createClearLocalOpMetadata(op, pendingClearMessageId, previousMap) {
    const localMetadata = {
        type: "clear",
        pendingMessageId: pendingClearMessageId,
        previousMap,
    };
    return localMetadata;
}
function createKeyLocalOpMetadata(op, pendingMessageId, previousValue) {
    const localMetadata = previousValue
        ? { type: "edit", pendingMessageId, previousValue }
        : { type: "add", pendingMessageId };
    return localMetadata;
}
/**
 * A SharedMap is a map-like distributed data structure.
 */
export class MapKernel {
    /**
     * The number of key/value pairs stored in the map.
     */
    get size() {
        return this.data.size;
    }
    /**
     * Create a new shared map kernel.
     * @param serializer - The serializer to serialize / parse handles
     * @param handle - The handle of the shared object using the kernel
     * @param submitMessage - A callback to submit a message through the shared object
     * @param isAttached - To query whether the shared object should generate ops
     * @param valueTypes - The value types to register
     * @param eventEmitter - The object that will emit map events
     */
    constructor(serializer, handle, submitMessage, isAttached, eventEmitter) {
        this.serializer = serializer;
        this.handle = handle;
        this.submitMessage = submitMessage;
        this.isAttached = isAttached;
        this.eventEmitter = eventEmitter;
        /**
         * Mapping of op types to message handlers.
         */
        this.messageHandlers = new Map();
        /**
         * The in-memory data the map is storing.
         */
        this.data = new Map();
        /**
         * Keys that have been modified locally but not yet ack'd from the server.
         */
        this.pendingKeys = new Map();
        /**
         * This is used to assign a unique id to every outgoing operation and helps in tracking unack'd ops.
         */
        this.pendingMessageId = -1;
        /**
         * The pending ids of any clears that have been performed locally but not yet ack'd from the server
         */
        this.pendingClearMessageIds = [];
        this.localValueMaker = new LocalValueMaker();
        this.messageHandlers = this.getMessageHandlers();
    }
    /**
     * Get an iterator over the keys in this map.
     * @returns The iterator
     */
    keys() {
        return this.data.keys();
    }
    /**
     * Get an iterator over the entries in this map.
     * @returns The iterator
     */
    // TODO: Use `unknown` instead (breaking change).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    entries() {
        const localEntriesIterator = this.data.entries();
        const iterator = {
            next() {
                const nextVal = localEntriesIterator.next();
                return nextVal.done
                    ? { value: undefined, done: true }
                    : // Unpack the stored value
                        { value: [nextVal.value[0], nextVal.value[1].value], done: false };
            },
            [Symbol.iterator]() {
                return this;
            },
        };
        return iterator;
    }
    /**
     * Get an iterator over the values in this map.
     * @returns The iterator
     */
    // TODO: Use `unknown` instead (breaking change).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values() {
        const localValuesIterator = this.data.values();
        const iterator = {
            next() {
                const nextVal = localValuesIterator.next();
                return nextVal.done
                    ? { value: undefined, done: true }
                    : // Unpack the stored value
                        { value: nextVal.value.value, done: false };
            },
            [Symbol.iterator]() {
                return this;
            },
        };
        return iterator;
    }
    /**
     * Get an iterator over the entries in this map.
     * @returns The iterator
     */
    // TODO: Use `unknown` instead (breaking change).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [Symbol.iterator]() {
        return this.entries();
    }
    /**
     * Executes the given callback on each entry in the map.
     * @param callbackFn - Callback function
     */
    forEach(callbackFn) {
        // eslint-disable-next-line unicorn/no-array-for-each
        this.data.forEach((localValue, key, m) => {
            callbackFn(localValue.value, key, m);
        });
    }
    /**
     * {@inheritDoc ISharedMap.get}
     */
    // TODO: Use `unknown` instead (breaking change).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(key) {
        const localValue = this.data.get(key);
        return localValue === undefined ? undefined : localValue.value;
    }
    /**
     * Check if a key exists in the map.
     * @param key - The key to check
     * @returns True if the key exists, false otherwise
     */
    has(key) {
        return this.data.has(key);
    }
    /**
     * {@inheritDoc ISharedMap.set}
     */
    set(key, value) {
        // Undefined/null keys can't be serialized to JSON in the manner we currently snapshot.
        if (key === undefined || key === null) {
            throw new Error("Undefined and null keys are not supported");
        }
        // Create a local value and serialize it.
        const localValue = this.localValueMaker.fromInMemory(value);
        // Set the value locally.
        const previousValue = this.setCore(key, localValue, true);
        // If we are not attached, don't submit the op.
        if (!this.isAttached()) {
            return;
        }
        const op = {
            key,
            type: "set",
            value: { type: localValue.type, value: localValue.value },
        };
        this.submitMapKeyMessage(op, previousValue);
    }
    /**
     * Delete a key from the map.
     * @param key - Key to delete
     * @returns True if the key existed and was deleted, false if it did not exist
     */
    delete(key) {
        // Delete the key locally first.
        const previousValue = this.deleteCore(key, true);
        // If we are not attached, don't submit the op.
        if (!this.isAttached()) {
            return previousValue !== undefined;
        }
        const op = {
            key,
            type: "delete",
        };
        this.submitMapKeyMessage(op, previousValue);
        return previousValue !== undefined;
    }
    /**
     * Clear all data from the map.
     */
    clear() {
        const copy = this.isAttached() ? new Map(this.data) : undefined;
        // Clear the data locally first.
        this.clearCore(true);
        // Clear the pendingKeys immediately, the local unack'd operations are aborted
        this.pendingKeys.clear();
        // If we are not attached, don't submit the op.
        if (!this.isAttached()) {
            return;
        }
        const op = {
            type: "clear",
        };
        this.submitMapClearMessage(op, copy);
    }
    /**
     * Serializes the data stored in the shared map to a JSON string
     * @param serializer - The serializer to use to serialize handles in its values.
     * @returns A JSON string containing serialized map data
     */
    getSerializedStorage(serializer) {
        const serializableMapData = {};
        for (const [key, localValue] of this.data.entries()) {
            serializableMapData[key] = localValue.makeSerialized(serializer, this.handle);
        }
        return serializableMapData;
    }
    getSerializableStorage(serializer) {
        const serializableMapData = {};
        for (const [key, localValue] of this.data.entries()) {
            serializableMapData[key] = makeSerializable(localValue, serializer, this.handle);
        }
        return serializableMapData;
    }
    serialize(serializer) {
        return JSON.stringify(this.getSerializableStorage(serializer));
    }
    /**
     * Populate the kernel with the given map data.
     * @param data - A JSON string containing serialized map data
     */
    populateFromSerializable(json) {
        for (const [key, serializable] of Object.entries(this.serializer.decode(json))) {
            const localValue = {
                key,
                value: this.makeLocal(key, serializable),
            };
            this.data.set(localValue.key, localValue.value);
        }
    }
    /**
     * Submit the given op if a handler is registered.
     * @param op - The operation to attempt to submit
     * @param localOpMetadata - The local metadata associated with the op. This is kept locally by the runtime
     * and not sent to the server. This will be sent back when this message is received back from the server. This is
     * also sent if we are asked to resubmit the message.
     * @returns True if the operation was submitted, false otherwise.
     */
    trySubmitMessage(op, localOpMetadata) {
        const handler = this.messageHandlers.get(op.type);
        if (handler === undefined) {
            return false;
        }
        handler.submit(op, localOpMetadata);
        return true;
    }
    tryApplyStashedOp(op) {
        switch (op.type) {
            case "clear": {
                this.clear();
                break;
            }
            case "delete": {
                this.delete(op.key);
                break;
            }
            case "set": {
                this.set(op.key, this.makeLocal(op.key, op.value).value);
                break;
            }
            default: {
                unreachableCase(op);
            }
        }
    }
    /**
     * Process the given op if a handler is registered.
     * @param op - The message to process
     * @param local - Whether the message originated from the local client
     * @param localOpMetadata - For local client messages, this is the metadata that was submitted with the message.
     * For messages from a remote client, this will be undefined.
     * @returns True if the operation was processed, false otherwise.
     */
    tryProcessMessage(op, local, localOpMetadata) {
        const handler = this.messageHandlers.get(op.type);
        if (handler === undefined) {
            return false;
        }
        handler.process(op, local, localOpMetadata);
        return true;
    }
    /* eslint-disable @typescript-eslint/no-unsafe-member-access */
    /**
     * Rollback a local op
     * @param op - The operation to rollback
     * @param localOpMetadata - The local metadata associated with the op.
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    rollback(op, localOpMetadata) {
        if (!isMapLocalOpMetadata(localOpMetadata)) {
            throw new Error("Invalid localOpMetadata");
        }
        if (op.type === "clear" && localOpMetadata.type === "clear") {
            if (localOpMetadata.previousMap === undefined) {
                throw new Error("Cannot rollback without previous map");
            }
            for (const [key, localValue] of localOpMetadata.previousMap.entries()) {
                this.setCore(key, localValue, true);
            }
            const lastPendingClearId = this.pendingClearMessageIds.pop();
            if (lastPendingClearId === undefined ||
                lastPendingClearId !== localOpMetadata.pendingMessageId) {
                throw new Error("Rollback op does match last clear");
            }
        }
        else if (op.type === "delete" || op.type === "set") {
            if (localOpMetadata.type === "add") {
                this.deleteCore(op.key, true);
            }
            else if (localOpMetadata.type === "edit" &&
                localOpMetadata.previousValue !== undefined) {
                this.setCore(op.key, localOpMetadata.previousValue, true);
            }
            else {
                throw new Error("Cannot rollback without previous value");
            }
            const pendingMessageIds = this.pendingKeys.get(op.key);
            const lastPendingMessageId = pendingMessageIds?.pop();
            if (!pendingMessageIds || lastPendingMessageId !== localOpMetadata.pendingMessageId) {
                throw new Error("Rollback op does not match last pending");
            }
            if (pendingMessageIds.length === 0) {
                this.pendingKeys.delete(op.key);
            }
        }
        else {
            throw new Error("Unsupported op for rollback");
        }
    }
    /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    /**
     * Set implementation used for both locally sourced sets as well as incoming remote sets.
     * @param key - The key being set
     * @param value - The value being set
     * @param local - Whether the message originated from the local client
     * @returns Previous local value of the key, if any
     */
    setCore(key, value, local) {
        const previousLocalValue = this.data.get(key);
        const previousValue = previousLocalValue?.value;
        this.data.set(key, value);
        this.eventEmitter.emit("valueChanged", { key, previousValue }, local, this.eventEmitter);
        return previousLocalValue;
    }
    /**
     * Clear implementation used for both locally sourced clears as well as incoming remote clears.
     * @param local - Whether the message originated from the local client
     */
    clearCore(local) {
        this.data.clear();
        this.eventEmitter.emit("clear", local, this.eventEmitter);
    }
    /**
     * Delete implementation used for both locally sourced deletes as well as incoming remote deletes.
     * @param key - The key being deleted
     * @param local - Whether the message originated from the local client
     * @returns Previous local value of the key if it existed, undefined if it did not exist
     */
    deleteCore(key, local) {
        const previousLocalValue = this.data.get(key);
        const previousValue = previousLocalValue?.value;
        const successfullyRemoved = this.data.delete(key);
        if (successfullyRemoved) {
            this.eventEmitter.emit("valueChanged", { key, previousValue }, local, this.eventEmitter);
        }
        return previousLocalValue;
    }
    /**
     * Clear all keys in memory in response to a remote clear, but retain keys we have modified but not yet been ack'd.
     */
    clearExceptPendingKeys() {
        // Assuming the pendingKeys is small and the map is large
        // we will get the value for the pendingKeys and clear the map
        const temp = new Map();
        for (const key of this.pendingKeys.keys()) {
            // Verify if the most recent pending operation is a delete op, no need to retain it if so.
            // This ensures the map size remains consistent.
            if (this.data.has(key)) {
                temp.set(key, this.data.get(key));
            }
        }
        this.clearCore(false);
        for (const [key, value] of temp.entries()) {
            this.setCore(key, value, true);
        }
    }
    /**
     * The remote ISerializableValue we're receiving (either as a result of a load or an incoming set op) will
     * have the information we need to create a real object, but will not be the real object yet.  For example,
     * we might know it's a map and the map's ID but not have the actual map or its data yet.  makeLocal's
     * job is to convert that information into a real object for local usage.
     * @param key - The key that the caller intends to store the local value into (used for ops later).  But
     * doesn't actually store the local value into that key.  So better not lie!
     * @param serializable - The remote information that we can convert into a real object
     * @returns The local value that was produced
     */
    // eslint-disable-next-line import/no-deprecated
    makeLocal(key, serializable) {
        if (serializable.type === ValueType[ValueType.Plain] ||
            serializable.type === ValueType[ValueType.Shared]) {
            return this.localValueMaker.fromSerializable(serializable, this.serializer, this.handle);
        }
        else {
            throw new Error("Unknown local value type");
        }
    }
    /**
     * If our local operations that have not yet been ack'd will eventually overwrite an incoming operation, we should
     * not process the incoming operation.
     * @param op - Operation to check
     * @param local - Whether the message originated from the local client
     * @param localOpMetadata - For local client messages, this is the metadata that was submitted with the message.
     * For messages from a remote client, this will be undefined.
     * @returns True if the operation should be processed, false otherwise
     */
    needProcessKeyOperation(op, local, localOpMetadata) {
        if (this.pendingClearMessageIds.length > 0) {
            if (local) {
                assert(localOpMetadata !== undefined &&
                    isMapKeyLocalOpMetadata(localOpMetadata) &&
                    // Non null asserting, above we checked that the length is greater than 0.
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    localOpMetadata.pendingMessageId < this.pendingClearMessageIds[0], 0x013 /* "Received out of order op when there is an unackd clear message" */);
            }
            // If we have an unack'd clear, we can ignore all ops.
            return false;
        }
        const pendingKeyMessageIds = this.pendingKeys.get(op.key);
        if (pendingKeyMessageIds !== undefined) {
            // Found an unack'd op. Clear it from the map if the pendingMessageId in the map matches this message's
            // and don't process the op.
            if (local) {
                assert(localOpMetadata !== undefined && isMapKeyLocalOpMetadata(localOpMetadata), 0x014 /* pendingMessageId is missing from the local client's operation */);
                assert(pendingKeyMessageIds[0] === localOpMetadata.pendingMessageId, 0x2fa /* Unexpected pending message received */);
                pendingKeyMessageIds.shift();
                if (pendingKeyMessageIds.length === 0) {
                    this.pendingKeys.delete(op.key);
                }
            }
            return false;
        }
        // If we don't have a NACK op on the key, we need to process the remote ops.
        return !local;
    }
    /**
     * Get the message handlers for the map.
     * @returns A map of string op names to IMapMessageHandlers for those ops
     */
    getMessageHandlers() {
        const messageHandlers = new Map();
        messageHandlers.set("clear", {
            process: (op, local, localOpMetadata) => {
                if (local) {
                    assert(isClearLocalOpMetadata(localOpMetadata), 0x015 /* "pendingMessageId is missing from the local client's clear operation" */);
                    const pendingClearMessageId = this.pendingClearMessageIds.shift();
                    assert(pendingClearMessageId === localOpMetadata.pendingMessageId, 0x2fb /* pendingMessageId does not match */);
                    return;
                }
                if (this.pendingKeys.size > 0) {
                    this.clearExceptPendingKeys();
                    return;
                }
                this.clearCore(local);
            },
            submit: (op, localOpMetadata) => {
                assert(isClearLocalOpMetadata(localOpMetadata), 0x2fc /* Invalid localOpMetadata for clear */);
                // We don't reuse the metadata pendingMessageId but send a new one on each submit.
                const pendingClearMessageId = this.pendingClearMessageIds.shift();
                assert(pendingClearMessageId === localOpMetadata.pendingMessageId, 0x2fd /* pendingMessageId does not match */);
                this.submitMapClearMessage(op, localOpMetadata.previousMap);
            },
        });
        messageHandlers.set("delete", {
            process: (op, local, localOpMetadata) => {
                if (!this.needProcessKeyOperation(op, local, localOpMetadata)) {
                    return;
                }
                this.deleteCore(op.key, local);
            },
            submit: (op, localOpMetadata) => {
                this.resubmitMapKeyMessage(op, localOpMetadata);
            },
        });
        messageHandlers.set("set", {
            process: (op, local, localOpMetadata) => {
                if (!this.needProcessKeyOperation(op, local, localOpMetadata)) {
                    return;
                }
                // needProcessKeyOperation should have returned false if local is true
                const context = this.makeLocal(op.key, op.value);
                this.setCore(op.key, context, local);
            },
            submit: (op, localOpMetadata) => {
                this.resubmitMapKeyMessage(op, localOpMetadata);
            },
        });
        return messageHandlers;
    }
    getMapClearMessageId() {
        const pendingMessageId = ++this.pendingMessageId;
        this.pendingClearMessageIds.push(pendingMessageId);
        return pendingMessageId;
    }
    /**
     * Submit a clear message to remote clients.
     * @param op - The clear message
     */
    submitMapClearMessage(op, previousMap) {
        const metadata = createClearLocalOpMetadata(op, this.getMapClearMessageId(), previousMap);
        this.submitMessage(op, metadata);
    }
    getMapKeyMessageId(op) {
        const pendingMessageId = ++this.pendingMessageId;
        const pendingMessageIds = this.pendingKeys.get(op.key);
        if (pendingMessageIds === undefined) {
            this.pendingKeys.set(op.key, [pendingMessageId]);
        }
        else {
            pendingMessageIds.push(pendingMessageId);
        }
        return pendingMessageId;
    }
    /**
     * Submit a map key message to remote clients.
     * @param op - The map key message
     * @param previousValue - The value of the key before this op
     */
    submitMapKeyMessage(op, previousValue) {
        const localMetadata = createKeyLocalOpMetadata(op, this.getMapKeyMessageId(op), previousValue);
        this.submitMessage(op, localMetadata);
    }
    /**
     * Submit a map key message to remote clients based on a previous submit.
     * @param op - The map key message
     * @param localOpMetadata - Metadata from the previous submit
     */
    resubmitMapKeyMessage(op, localOpMetadata) {
        assert(isMapKeyLocalOpMetadata(localOpMetadata), 0x2fe /* Invalid localOpMetadata in submit */);
        // no need to submit messages for op's that have been aborted
        const pendingMessageIds = this.pendingKeys.get(op.key);
        if (pendingMessageIds === undefined) {
            return;
        }
        const index = pendingMessageIds.indexOf(localOpMetadata.pendingMessageId);
        if (index === -1) {
            return;
        }
        pendingMessageIds.splice(index, 1);
        if (pendingMessageIds.length === 0) {
            this.pendingKeys.delete(op.key);
        }
        // We don't reuse the metadata pendingMessageId but send a new one on each submit.
        const pendingMessageId = this.getMapKeyMessageId(op);
        const localMetadata = localOpMetadata.type === "edit"
            ? { type: "edit", pendingMessageId, previousValue: localOpMetadata.previousValue }
            : { type: "add", pendingMessageId };
        this.submitMessage(op, localMetadata);
    }
}
//# sourceMappingURL=mapKernel.js.map